// =============================================================================
// app/src/lib/aVerPdf.ts — o vale "a ver" como arquivo PDF.
//
// É o MESMO documento que o aVerDoc.ts imprime, por outro caminho: uma folha
// A4, duas vias, ambas assinadas. A diferença é o destino — este vira arquivo
// que se manda no WhatsApp do cliente, se guarda numa pasta e se imprime em
// qualquer lugar depois. O outro vai direto para a impressora da loja.
//
// O QUE NÃO PODE DIVERGIR: as regras do que o papel DIZ moram no aVerDoc.ts e
// são importadas aqui — `tarjaDe` (o vale ainda vale?) e `dataDeEmissao` (a
// data do papel, não a da digitação). O dia em que o HTML disser "CANCELADO" e
// o PDF não é o dia em que a loja honra um vale morto.
//
// A marca sai como palavra, sem o símbolo S. O logo é um par de caminhos Bézier
// no SVG e convertê-los para PDF renderia um módulo inteiro só para isso —
// desproporcional para um documento cuja função é ser assinado. A tipografia em
// caixa alta com o "AUTO PEÇAS" abaixo é o mesmo arranjo do papel timbrado.
// =============================================================================
import type { StoreCredit } from '@core/types';
import { dataDeEmissao, dataPorExtenso, tarjaDe, type OpcoesAVerDoc } from './aVerDoc';
import { brl, dataBR, dataHoraBR } from './formato';
import { baixarPdf, novoPdf, quebrarEmLinhas, type Pdf } from './pdf';

const MARGEM = 16;
const DIREITA = 210 - MARGEM;
const LARGURA_UTIL = DIREITA - MARGEM;

/** Onde cada via começa. A segunda deixa espaço para a linha de corte. */
const TOPO_VIA = [14, 156];
const CORTE = 149;

const TERMOS_VALIDO = 'Este vale dá direito a crédito no valor acima para compra de peças'
  + ' nesta loja. Não é negociável, não é transferível e não pode ser convertido em'
  + ' dinheiro. Válido somente para o cliente identificado, mediante apresentação deste'
  + ' documento.';

const TERMOS_SEM_VALOR = 'Este documento é apenas o histórico do vale acima: não dá direito'
  + ' a crédito e não pode ser apresentado como vale válido. O código continua auditável'
  + ' no sistema da loja.';

/** Escreve o parágrafo a partir de `y` e devolve o y logo abaixo dele. */
function paragrafo(pdf: Pdf, texto: string, y: number, tamanho = 8, cinza = 0.2): number {
  const linhas = quebrarEmLinhas(texto, LARGURA_UTIL, tamanho);
  linhas.forEach((linha, i) => pdf.texto(linha, MARGEM, y + i * (tamanho * 0.42), { tamanho, cinza }));
  return y + linhas.length * (tamanho * 0.42);
}

const COLUNA_VALOR = MARGEM + 34;

/**
 * Uma linha da tabela de conferência. O valor QUEBRA: "Referente a" recebe
 * texto livre digitado pelo gerente ("devolução de amortecedor dianteiro
 * direito com defeito de fábrica constatado pelo mecânico…") e, sem quebra,
 * ele sai pela margem direita e some da folha. No HTML a `<td>` fazia isso
 * sozinha; no PDF não existe fluxo de texto, cada linha é uma coordenada.
 */
function linhaDaTabela(pdf: Pdf, rotulo: string, valor: string, y: number): number {
  pdf.texto(rotulo, MARGEM, y, { tamanho: 7.5, cinza: 0.45 });
  const linhas = quebrarEmLinhas(valor, DIREITA - COLUNA_VALOR, 9);
  linhas.forEach((linha, i) => pdf.texto(linha, COLUNA_VALOR, y + i * 4.2, { tamanho: 9 }));
  return y + Math.max(1, linhas.length) * 4.2 + 1.2;
}

function via(pdf: Pdf, credito: StoreCredit, rotuloVia: string, opcoes: Required<OpcoesAVerDoc>) {
  const { emissao, agora } = opcoes;
  const topo = TOPO_VIA[rotuloVia === 'Via do cliente' ? 0 : 1]!;
  const tarja = emissao ? null : tarjaDe(credito, agora);
  const semValor = tarja !== null;
  const original = brl(Number(credito.valor_original));

  // Cabeçalho.
  pdf.texto('SERGINHO', MARGEM, topo + 6, { tamanho: 15, negrito: true });
  pdf.texto('AUTO PEÇAS', MARGEM, topo + 10.5, { tamanho: 7.5, cinza: 0.35 });
  pdf.texto(`VALE "A VER"${emissao ? '' : ' · REIMPRESSÃO'}`, DIREITA, topo + 5,
    { tamanho: 8.5, cinza: 0.35, alinhamento: 'direita' });
  pdf.texto(credito.codigo, DIREITA, topo + 11.5,
    { tamanho: 14, negrito: true, alinhamento: 'direita' });
  pdf.linha(MARGEM, topo + 14, DIREITA, topo + 14, { espessura: 0.4 });

  let y = topo + 21;

  if (tarja) {
    pdf.retangulo(MARGEM, y - 4.5, LARGURA_UTIL, 7, 0.82);
    pdf.texto(tarja, 105, y, { tamanho: 9.5, negrito: true, alinhamento: 'centro' });
    y += 9;
  }

  // O valor. Na emissão é o contratado; na 2ª via é o saldo de HOJE, com o
  // original ao lado para conferência — reimpressão que mente sobre o saldo é
  // briga de balcão.
  pdf.retangulo(MARGEM, y - 5, LARGURA_UTIL, 17, semValor ? 0.93 : 0.9);
  pdf.texto(emissao ? 'Valor em crédito' : 'Saldo disponível', MARGEM + 4, y,
    { tamanho: 7.5, cinza: 0.4 });
  if (!emissao) {
    pdf.texto(`em ${dataHoraBR(agora.toISOString())} · valor original ${original}`,
      MARGEM + 4, y + 4.5, { tamanho: 7, cinza: 0.45 });
  }
  pdf.texto(emissao ? original : brl(Number(credito.saldo)), DIREITA - 4, y + 6,
    { tamanho: 19, negrito: true, alinhamento: 'direita' });
  y += 18;

  y = linhaDaTabela(pdf, 'Cliente', credito.cliente_nome, y);
  if (credito.cliente_doc) y = linhaDaTabela(pdf, 'CPF/CNPJ', credito.cliente_doc, y);
  if (credito.motivo) y = linhaDaTabela(pdf, 'Referente a', credito.motivo, y);
  y = linhaDaTabela(pdf, 'Emitido em', dataBR(dataDeEmissao(credito)), y);
  y = linhaDaTabela(pdf, 'Válido até',
    credito.valido_ate ? dataBR(`${credito.valido_ate}T12:00:00`) : 'sem vencimento', y);

  y += 2;
  y = paragrafo(pdf, semValor
    ? TERMOS_SEM_VALOR
    : `${TERMOS_VALIDO} ${emissao
      ? 'Pode ser usado em mais de uma compra até esgotar o saldo.'
      : 'Vale o SALDO impresso acima, apurado na data desta reimpressão — compras posteriores o reduzem.'}`,
  y);

  if (!semValor) {
    y += 4;
    pdf.texto(`Rio Grande/RS, ${dataPorExtenso(dataDeEmissao(credito))}.`, MARGEM, y,
      { tamanho: 8.5 });

    // As duas assinaturas. O vão de 14 mm acima da linha é onde a caneta passa:
    // com menos, a assinatura invade a linha da data (medido no papel).
    y += 18;
    const meio = MARGEM + LARGURA_UTIL / 2;
    const colunas: Array<[number, number, string, string]> = [
      [MARGEM + 6, meio - 6, 'Serginho Autopeças', opcoes.emitidoPor || 'Responsável pela emissão'],
      [meio + 6, DIREITA - 6, credito.cliente_nome, credito.cliente_doc || 'Cliente'],
    ];
    for (const [x1, x2, nome, sob] of colunas) {
      pdf.linha(x1, y, x2, y, { espessura: 0.3 });
      pdf.texto(nome, (x1 + x2) / 2, y + 4, { tamanho: 8.5, negrito: true, alinhamento: 'centro' });
      pdf.texto(sob, (x1 + x2) / 2, y + 8, { tamanho: 7.5, cinza: 0.4, alinhamento: 'centro' });
    }
    y += 15;

    pdf.texto(`Para usar: apresente esta via e informe o código ${credito.codigo} no balcão.`,
      MARGEM, y, { tamanho: 8, negrito: true });
    y += 5;
  }

  pdf.texto(
    `${rotuloVia} · ${credito.codigo}${emissao ? '' : ` · reimpresso em ${dataHoraBR(agora.toISOString())}`}`,
    MARGEM, y + 2, { tamanho: 7, cinza: 0.5 },
  );
}

/** O documento inteiro: duas vias numa folha A4, com a linha de corte no meio. */
export function montarPdfAVer(credito: StoreCredit, opcoes: OpcoesAVerDoc = {}): Uint8Array {
  const completas: Required<OpcoesAVerDoc> = {
    emissao: opcoes.emissao ?? false,
    emitidoPor: opcoes.emitidoPor ?? null,
    agora: opcoes.agora ?? new Date(),
  };
  const pdf = novoPdf();
  via(pdf, credito, 'Via do cliente', completas);

  // Linha de corte: tracejada seria mais bonita, mas o operador `d` do PDF é
  // mais uma coisa para dar errado num módulo escrito à mão. Um traço fino
  // claro cumpre o papel de dizer onde a tesoura vai.
  pdf.linha(MARGEM, CORTE, DIREITA, CORTE, { espessura: 0.2, cinza: 0.6 });
  pdf.texto('corte aqui', 105, CORTE - 1.5, { tamanho: 6.5, cinza: 0.6, alinhamento: 'centro' });

  via(pdf, credito, 'Via da loja', completas);
  return pdf.bytes();
}

/** Nome de arquivo previsível: o código do vale é o que a pessoa vai procurar. */
export function nomeDoArquivoAVer(credito: StoreCredit): string {
  const cliente = credito.cliente_nome
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30);
  return `vale-${credito.codigo}${cliente ? `-${cliente}` : ''}.pdf`;
}

export function baixarAVer(credito: StoreCredit, opcoes: OpcoesAVerDoc = {}): void {
  baixarPdf(montarPdfAVer(credito, opcoes), nomeDoArquivoAVer(credito));
}
